import { PureComponent } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { ThunkDispatch } from 'redux-thunk';

import { getDoc, getPatientByLocalId, upsertPrescription } from '../store/idbHandler';
import { GO_TO_PRESCRIPTION_PAD } from '../routes/paths';
import { GlobalState } from '../store/reducers';
import { CreateVisitAction } from '../store/actions/visit';
import fetchPrescriptionById, { Px } from '../api/fetchPrescriptionById';
import { Prescription } from '../store/types/prescription';

interface Props extends RouteComponentProps<{ pxId: string }> {
    dispatch: ThunkDispatch<GlobalState, null, CreateVisitAction>;
}

class FetchUpsertPrescription extends PureComponent<Props> {
    componentDidMount = async (): Promise<void> => {
        const { pxId } = this.props.match.params;

        // fetch from api
        let px: Px | undefined;

        try {
            px = await fetchPrescriptionById(pxId);
        } catch (e) {
            console.error(e);
        }

        if (!px) {
            console.error('Px not found');
            this.props.history.goBack();
            return;
        }

        const patient = await getPatientByLocalId(px?.patient._id);
        if (!patient) {
            console.error('Pt not found');
            this.props.history.goBack();
            return;
        }

        const doc = px.doctor || getDoc();

        if (!doc) {
            console.error('doc not found');
            this.props.history.goBack();
            return;
        }

        if (px) {
            const clinic =
                px.selectedClinicData ||
                doc.profile.professional.clinics?.find(
                    (c) => c._id === doc.profile.professional.default_clinic,
                );

            if (!clinic) {
                console.log('clinic not found');
                return;
            }

            const pres: Prescription = {
                id: px.id,
                date: new Date(px.date),
                doctor: doc,
                patient: {
                    id: patient.id,
                    localId: patient.localId,
                    uuid: patient.uuid,
                    profile: {
                        personal: {
                            name: [patient.personal.name.f, patient.personal.name.l]
                                .filter(Boolean)
                                .join(' '),
                            phone: patient.personal.phone,
                            age: patient.personal.age,
                            gender: patient.personal.gender,
                        },
                    },
                    formData: patient.formData,
                },
                visitId: px.visitid,
                clinic,
                examinations: px.tool.medicalHistory?.examinations,
                diagnosis: px.tool.diagnosis,
                symptoms: px.tool.symptoms,
                vitals: px.tool.medicalHistory?.vitals,
                medications: px.tool.medications,
                labTests: px.tool.labTests,
                followup: px.tool.followup,
                advices: px.tool.advices,
                patientHistory: px.tool.medicalHistory?.patientHistory,
                prescriptionNotes: px.tool.prescriptionNotes,
                privateNotes: px.tool.privateNotes,
                dyform: px.tool.dyform,
                refer: px.tool.refer,
                labRefer: px.tool.labRefer,
                labVitals: px.tool.labVitals,
                attachment: px.tool?.attachment,
                dentalExaminations: px.tool?.dentalExaminations,
                dentalProcedures: px.tool?.dentalProcedures,
                opVision: px.tool.opVision,
                opIop: px.tool.opIop,
                opCurrentSpec: px.tool.opCurrentSpec,
                opSubjectiveRefraction: px.tool.opSubjectiveRefraction,
                opAutoRefraction: px.tool.opAutoRefraction,
                opFinalPrescription: px.tool.opFinalPrescription,
                injections: px.tool.injections,
                procedures: px.tool?.procedures,
                ipdAdmission: px.tool?.ipdAdmission,
                careCanvas: px.tool?.careCanvas,
                administeredVaccines: px.tool?.medicalHistory?.patientHistory?.vaccinationHistory,
            };

            await upsertPrescription(pres);

            this.props.history.push(GO_TO_PRESCRIPTION_PAD(pxId, 'edit'));
            return;
        }
    };

    render() {
        return (
            <div className="flex flex-col items-center justify-center w-screen h-screen space-y-64">
                <div className="pulse">
                    <svg width="16px" height="12px">
                        <polyline id="back" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                        <polyline id="front" points="1 6 4 6 6 11 10 1 12 6 15 6"></polyline>
                    </svg>
                </div>
            </div>
        );
    }
}

export default connect()(FetchUpsertPrescription);
